'use client'

import { AnimatePresence, motion } from 'framer-motion'
import { GripVertical } from 'lucide-react'
import React, { useEffect, useState } from 'react'

// Context action interface
export interface ContextAction {
  id: string
  label: string
  icon?: React.ReactNode
  onClick: () => void
  disabled?: boolean
  variant?: 'default' | 'destructive'
}

interface ContextMenuWrapperProps {
  children: React.ReactNode
  actions?: ContextAction[]
  className?: string
  nested?: boolean // Kept for backward compatibility, but ignored in simplified system
}

export function ContextMenuWrapper({
  children,
  actions = [],
  className = '',
}: ContextMenuWrapperProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const hasActions = actions.length > 0

  // Click outside handler to close this menu
  useEffect(() => {
    if (!isOpen) return

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element
      if (!target.closest('[data-context-menu]') && !target.closest('[data-context-trigger]')) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsOpen(!isOpen)
  }

  const handleActionClick = (action: ContextAction) => {
    if (!action.disabled) {
      action.onClick()
      setIsOpen(false)
    }
  }

  const showTrigger = hasActions && (isHovered || isOpen)

  return (
    <div
      className="group relative"
      data-context-container
      onMouseEnter={() => hasActions && setIsHovered(true)}
      onMouseLeave={() => hasActions && setIsHovered(false)}
    >
      <div className={`px-8 ${className}`}>{children}</div>

      {/* Context Button - positioned at left edge, always centered on the header element */}
      {showTrigger && (
        <div className="absolute top-4">
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.12, ease: 'easeOut' }}
              data-context-trigger
            >
              <button
                onClick={handleTriggerClick}
                className={`group/btn flex h-6 w-6 items-center justify-center rounded-lg transition-colors ${
                  isOpen ? 'bg-ui-hover text-ui-text' : 'text-ui-text hover:bg-ui-hover bg-gray-100'
                }`}
                aria-label="Context menu"
              >
                <GripVertical className="h-4 w-4" />
              </button>
            </motion.div>
          </AnimatePresence>

          {/* Context Menu */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.12, ease: 'easeOut' }}
                className="font-hanken-grotesk absolute left-5 top-7 z-50 min-w-[160px] rounded-lg border border-gray-200 bg-white py-1.5 shadow-lg"
                data-context-menu
              >
                {actions.map((action) => (
                  <button
                    key={action.id}
                    onClick={() => handleActionClick(action)}
                    disabled={action.disabled}
                    className={`flex w-full items-center gap-3 px-3 py-1.5 text-left text-sm font-normal transition-colors ${
                      action.disabled
                        ? 'cursor-not-allowed opacity-40'
                        : action.variant === 'destructive'
                          ? 'hover:bg-ui-hover text-red-600'
                          : 'text-ui-text hover:bg-ui-hover'
                    }`}
                  >
                    {action.icon && (
                      <span className="text-ui-secondary h-4 w-4 flex-shrink-0">{action.icon}</span>
                    )}
                    <span className="truncate">{action.label}</span>
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}
